import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Star } from "lucide-react";
import { useSwipeable } from "react-swipeable";
import { useStarTracker } from "@/hooks/useStarTracker";
import { usePageLoadResetCheck } from "@/hooks/usePageLoadResetCheck";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TaskList } from "./tasks";
import { RewardList } from "./rewards";
import { WishListTab } from "./wishlist";
import { StatsTab } from "./stats";
import { WalletTab } from "./wallet";
import { StarTrackerHeader, StatusBar, BottomTabNavigation } from "./layout";
import { EditModePanel, AdminTab } from "./admin";
import { GiantStarAnimation, TrophyNotification } from "./animations";
import { ResetConfirmDialog } from "./dialogs";
import { BottomAd } from "./ads";
import { PerfectDayCelebration3D } from "./celebrations";
import {
  Task,
  Reward,
  Achievement,
  WalletSettings,
  ThemeName,
  TabSettings,
  MoneyTrackingEntry,
} from "@/utils/storage";
import { toast } from "sonner";
import { Starfield, OceanBackground, RainbowBackground, ForestBackground, SunsetBackground } from "./backgrounds";

interface StarTrackerProps {
  initialTab?: string;
}

const StarTracker: React.FC<StarTrackerProps> = ({ initialTab = "tasks" }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Perform page load reset checks
  usePageLoadResetCheck();

  // State for trophy notification
  const [showTrophyNotification, setShowTrophyNotification] = useState(false);
  const [claimedRewardTitle, setClaimedRewardTitle] = useState("");

  // State for perfect day celebration
  const [showPerfectDayCelebration, setShowPerfectDayCelebration] =
    useState<ThemeName | null>(null);

  // Local state for immediate UI updates
  const [localBottomNavigation, setLocalBottomNavigation] = useState<
    boolean | undefined
  >(undefined);
  const [localTabSettings, setLocalTabSettings] = useState<
    TabSettings | undefined
  >(undefined);
  const [localMoneyEntries, setLocalMoneyEntries] = useState<
    MoneyTrackingEntry[] | undefined
  >(undefined);

  const {
    data,
    updateData,
    editMode,
    activeTab,
    showResetDialog,
    showGiantStar,
    starsCounterRef,
    isAnimating,
    adsEnabled,
    setAdsEnabled,
    setActiveTab,
    setShowResetDialog,
    handleToggleEditMode,
    handleCompleteTask,
    handleClaimReward: originalHandleClaimReward,
    handleSpendMoney,
    handleThemeChange,
    handleDeleteItem,
    handleResetData,
    handleUpdateStars,
    handleUpdateTotalStars,
    handleResetDailyTasks,
    handleResetTask,
    handleMoveTask,
    handleMoveReward,
    handleMoveAchievement,
    getThemeEmoji,
    handleGiantStarAnimationComplete,
    // Wish List functionality
    wishList,
    wishListEnabled,
    addWishItem,
    updateWishItem,
    deleteWishItem,
    markWishItemAchieved,
    updateWishItemProgress,
    toggleWishListEnabled,
  } = useStarTracker(initialTab, (theme) => {
    setShowPerfectDayCelebration(theme as ThemeName);
  });

  // Sync local tab settings when data changes
  useEffect(() => {
    if (data?.tabSettings && localTabSettings === undefined) {
      setLocalTabSettings(data.tabSettings);
    }
  }, [data?.tabSettings, localTabSettings]);

  // Get available tabs in order for swipe navigation
  const getAvailableTabs = useCallback((): string[] => {
    if (!data) return [];

    const tabs: string[] = [];

    // Get effective tab settings with fallbacks (prefer local state for immediate updates)
    const effectiveTabSettings = localTabSettings !== undefined
      ? localTabSettings
      : data?.tabSettings || {
          tasksEnabled: true,
          rewardsEnabled: true,
          trophiesEnabled: true,
          wishListEnabled: false,
          walletEnabled: true,
        };

    if (effectiveTabSettings.tasksEnabled) {
      tabs.push("tasks");
    }

    // Always show rewards tab when rewards are enabled
    if (effectiveTabSettings.rewardsEnabled) {
      tabs.push("rewards");
    }

    // Show wallet tab when wallet feature is enabled AND wallet tab is visible
    if (data.walletSettings?.enabled && effectiveTabSettings.walletEnabled) {
      tabs.push("wallet");
    }

    if (wishListEnabled && effectiveTabSettings.wishListEnabled) {
      tabs.push("wishlist");
    }

    if (effectiveTabSettings.trophiesEnabled) {
      tabs.push("trophies");
    }

    if (editMode) {
      tabs.push("admin");
    }

    return tabs;
  }, [data, editMode, wishListEnabled, localTabSettings]);

  // Handle swipe gestures for tab navigation
  const handleSwipeLeft = useCallback(() => {
    const availableTabs = getAvailableTabs();
    if (availableTabs.length <= 1) return;

    const currentIndex = availableTabs.indexOf(activeTab);
    const nextIndex = (currentIndex + 1) % availableTabs.length;
    setActiveTab(availableTabs[nextIndex]);
  }, [getAvailableTabs, activeTab, setActiveTab]);

  const handleSwipeRight = useCallback(() => {
    const availableTabs = getAvailableTabs();
    if (availableTabs.length <= 1) return;

    const currentIndex = availableTabs.indexOf(activeTab);
    const prevIndex =
      currentIndex === 0 ? availableTabs.length - 1 : currentIndex - 1;
    setActiveTab(availableTabs[prevIndex]);
  }, [getAvailableTabs, activeTab, setActiveTab]);

  // Configure swipe handlers
  const swipeHandlers = useSwipeable({
    onSwipedLeft: handleSwipeLeft,
    onSwipedRight: handleSwipeRight,
    trackMouse: true, // Enable mouse swipe on desktop
    trackTouch: true, // Enable touch swipe on mobile
    preventScrollOnSwipe: false, // Allow vertical scrolling
    delta: 50, // Minimum distance for swipe detection
  });

  // Enhanced reward claiming with notification
  const handleClaimReward = (reward: Reward) => {
    setClaimedRewardTitle(reward.title);
    originalHandleClaimReward(reward);

    // Show notification after the star animation
    setTimeout(() => {
      setShowTrophyNotification(true);
    }, 1500);
  };

  // Listen for location changes to refresh data when returning from edit pages
  useEffect(() => {
    const state = location.state as { dataChanged?: boolean };
    if (state?.dataChanged === true) {
      // Force a data refresh
      window.location.reload();
    }
  }, [location.state]);

  // Listen for edit mode toggle from settings page
  useEffect(() => {
    const handleToggleEditModeEvent = () => {
      handleToggleEditMode();
    };

    window.addEventListener('toggleEditMode', handleToggleEditModeEvent);
    return () => {
      window.removeEventListener('toggleEditMode', handleToggleEditModeEvent);
    };
  }, [handleToggleEditMode]);

  const handleSyncData = async () => {
    if (!data) return;

    try {
      const appData = await updateData(data);
      if (appData) {
        // Data has been synced successfully
      }
    } catch (error) {
      console.error("Error syncing data:", error);
      toast.error("Failed to sync data");
    }
  };

  const handleWalletSettingsChange = async (newSettings: WalletSettings) => {
    if (!data) return;

    try {
      const updatedData = {
        ...data,
        walletSettings: newSettings,
      };

      const appData = await updateData(updatedData);
      if (appData) {
        toast.success("Wallet settings updated");

        // No automatic tab switching - let users decide which tab to view
        // Both rewards and wallet tabs can coexist when wallet is enabled
      }
    } catch (error) {
      console.error("Error updating wallet settings:", error);
      toast.error("Failed to update wallet settings");
    }
  };

  // Add effect to handle wallet state changes that should trigger UI updates
  useEffect(() => {
    // Only switch tabs if the current tab becomes unavailable
    if (!data?.walletSettings?.enabled && activeTab === "wallet") {
      // If wallet is disabled and we're currently on wallet tab, switch to rewards
      setActiveTab("rewards");
    }
    // Note: We no longer auto-switch from rewards to wallet when enabling wallet
    // Both tabs can coexist when wallet is enabled
  }, [data?.walletSettings?.enabled, activeTab, setActiveTab]);

  // Sync local state with data when it changes
  useEffect(() => {
    if (data?.bottomNavigation !== undefined) {
      setLocalBottomNavigation(data.bottomNavigation);
    }
  }, [data?.bottomNavigation]);

  // Sync money entries for immediate wallet UI updates
  useEffect(() => {
    if (data?.moneyTracking) {
      setLocalMoneyEntries(data.moneyTracking);
    }
  }, [data?.moneyTracking]);

  const handleBottomNavigationChange = async (enabled: boolean) => {
    if (!data) return;

    // Update local state immediately for UI feedback
    setLocalBottomNavigation(enabled);

    try {
      const updatedData = {
        ...data,
        bottomNavigation: enabled,
      };

      const appData = await updateData(updatedData);
      if (appData) {
        toast.success(`Navigation moved to ${enabled ? "bottom" : "top"}`);
      }
    } catch (error) {
      console.error("Error updating bottom navigation setting:", error);
      toast.error("Failed to update navigation setting");
      // Revert local state on error
      setLocalBottomNavigation(data.bottomNavigation);
    }
  };

  const handleTabSettingsChange = async (newTabSettings: TabSettings) => {
    if (!data) return;

    // Update local state immediately for instant UI feedback
    setLocalTabSettings(newTabSettings);

    try {
      const shouldSyncWalletEnabled =
        data.walletSettings?.enabled !== newTabSettings.walletEnabled;

      const updatedData = {
        ...data,
        tabSettings: newTabSettings,
        walletSettings: shouldSyncWalletEnabled
          ? {
              ...(data.walletSettings || { conversionRate: 10 }),
              enabled: newTabSettings.walletEnabled,
            }
          : data.walletSettings,
      };

      const appData = await updateData(updatedData);
      if (appData) {
        toast.success("Tab visibility settings updated");
      }
    } catch (error) {
      console.error("Error updating tab settings:", error);
      toast.error("Failed to update tab settings");
      // Revert local state on error
      setLocalTabSettings(data.tabSettings);
    }
  };

  // Money tracking handlers
  const handleAddMoneyEntry = async (entry: Omit<MoneyTrackingEntry, "id" | "dateAdded">) => {
    if (!data) return;

    const newEntry: MoneyTrackingEntry = {
      id: `money_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
      title: entry.title,
      amount: entry.amount,
      dateAdded: new Date().toISOString(),
    };

    try {
      const updatedEntries = [...(localMoneyEntries || data.moneyTracking || []), newEntry];
      setLocalMoneyEntries(updatedEntries);

      const updatedData = {
        ...data,
        moneyTracking: updatedEntries,
      };

      const appData = await updateData(updatedData);
      if (appData) {
        toast.success("Money source added");
      }
    } catch (error) {
      console.error("Error adding money entry:", error);
      toast.error("Failed to add money source");
      setLocalMoneyEntries(data.moneyTracking || []);
    }
  };

  const handleUpdateMoneyEntry = async (id: string, updates: Partial<MoneyTrackingEntry>) => {
    if (!data) return;

    try {
      const updatedEntries = (localMoneyEntries || data.moneyTracking || []).map((entry) =>
        entry.id === id ? { ...entry, ...updates } : entry
      );
      setLocalMoneyEntries(updatedEntries);

      const updatedData = {
        ...data,
        moneyTracking: updatedEntries,
      };

      const appData = await updateData(updatedData);
      if (appData) {
        toast.success("Money source updated");
      }
    } catch (error) {
      console.error("Error updating money entry:", error);
      toast.error("Failed to update money source");
      setLocalMoneyEntries(data.moneyTracking || []);
    }
  };

  const handleDeleteMoneyEntry = async (id: string) => {
    if (!data) return;

    try {
      const updatedEntries = (localMoneyEntries || data.moneyTracking || []).filter(
        (entry) => entry.id !== id
      );
      setLocalMoneyEntries(updatedEntries);

      const updatedData = {
        ...data,
        moneyTracking: updatedEntries,
      };

      const appData = await updateData(updatedData);
      if (appData) {
        toast.success("Money source deleted");
      }
    } catch (error) {
      console.error("Error deleting money entry:", error);
      toast.error("Failed to delete money source");
      setLocalMoneyEntries(data.moneyTracking || []);
    }
  };

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse-scale">
          <Star size={48} className="text-primary" />
        </div>
      </div>
    );
  }

  // Get the effective bottom navigation state (prefer local state for immediate updates)
  const effectiveBottomNavigation =
    localBottomNavigation !== undefined
      ? localBottomNavigation
      : data?.bottomNavigation || false;

  // Get effective tab settings with fallbacks (prefer local state for immediate updates)
  const effectiveTabSettings = localTabSettings !== undefined
    ? localTabSettings
    : data?.tabSettings || {
        tasksEnabled: true,
        rewardsEnabled: true,
        trophiesEnabled: true,
        wishListEnabled: false,
        walletEnabled: true,
      };

  const handleEditItem = (
    item: Task | Reward | Achievement,
    type: "task" | "reward" | "achievement"
  ) => {
    navigate(`/edit/${type}/${item.id}`, {
      state: { fromEditMode: editMode },
    });
  };

  const handleAddNew = (type: "task" | "reward" | "achievement") => {
    navigate(`/edit/${type}/new`, {
      state: { fromEditMode: editMode },
    });
  };

  // Render appropriate background based on theme
  const renderThemeBackground = () => {
    if (!data) return null;

    switch (data.theme) {
      case "space":
        return (
          <div
            key="space-bg"
            className="transition-opacity duration-500 ease-in-out"
          >
            <Starfield />
          </div>
        );
      case "ocean":
        return (
          <div
            key="ocean-bg"
            className="transition-opacity duration-500 ease-in-out"
          >
            <OceanBackground />
          </div>
        );
      case "unicorn":
        return (
          <div
            key="unicorn-bg"
            className="transition-opacity duration-500 ease-in-out"
          >
            <RainbowBackground />
          </div>
        );
      case "robots":
      default:
        return null;
    }
  };

  return (
    <div
      {...swipeHandlers}
      className={`min-h-screen p-4 sm:p-6 transition-all duration-300 iphone-safe-area main-content-area ${
        editMode ? "bg-gray-50 dark:bg-gray-900" : ""
      } ${
        effectiveBottomNavigation
          ? "bottom-nav-content-padding-fallback bottom-nav-content-padding"
          : "pb-20"
      }`}
    >
      {/* Theme-specific animated background */}
      {renderThemeBackground()}

      <div className="max-w-3xl mx-auto">
        <StarTrackerHeader
          stars={data.stars}
          editMode={editMode}
          themeEmoji={getThemeEmoji(data.theme)}
          starsCounterRef={starsCounterRef}
          onUpdateTotalStars={handleUpdateTotalStars}
          streakDays={data.streakDays}
        />

        <div className="mb-6">
          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
            className="w-full"
          >
            {/* Conditionally render top navigation tabs only if bottom navigation is disabled */}
            {effectiveBottomNavigation !== true && (
              <TabsList
                className={`grid w-full mb-6 ${
                  getAvailableTabs().length === 1
                    ? "grid-cols-1"
                    : getAvailableTabs().length === 2
                    ? "grid-cols-2"
                    : getAvailableTabs().length === 3
                    ? "grid-cols-3"
                    : getAvailableTabs().length === 4
                    ? "grid-cols-4"
                    : getAvailableTabs().length === 5
                    ? "grid-cols-5"
                    : getAvailableTabs().length === 6
                    ? "grid-cols-6"
                    : "grid-cols-3"
                }`}
              >
                {effectiveTabSettings.tasksEnabled && (
                  <TabsTrigger
                    value="tasks"
                    className="relative overflow-hidden group"
                  >
                    <span className="relative z-10">Tasks</span>
                    <span className="absolute inset-0 bg-primary/10 scale-0 group-hover:scale-100 transition-transform duration-300 rounded-md"></span>
                  </TabsTrigger>
                )}
                {effectiveTabSettings.rewardsEnabled && (
                  <TabsTrigger
                    value="rewards"
                    className="relative overflow-hidden group"
                  >
                    <span className="relative z-10">Rewards</span>
                    <span className="absolute inset-0 bg-primary/10 scale-0 group-hover:scale-100 transition-transform duration-300 rounded-md"></span>
                  </TabsTrigger>
                )}
                {data.walletSettings?.enabled &&
                  effectiveTabSettings.walletEnabled && (
                    <TabsTrigger
                      value="wallet"
                      className="relative overflow-hidden group"
                    >
                      <span className="relative z-10">Wallet</span>
                      <span className="absolute inset-0 bg-primary/10 scale-0 group-hover:scale-100 transition-transform duration-300 rounded-md"></span>
                    </TabsTrigger>
                  )}
                {wishListEnabled && effectiveTabSettings.wishListEnabled && (
                  <TabsTrigger
                    value="wishlist"
                    className="relative overflow-hidden group"
                  >
                    <span className="relative z-10">Wish List</span>
                    <span className="absolute inset-0 bg-primary/10 scale-0 group-hover:scale-100 transition-transform duration-300 rounded-md"></span>
                  </TabsTrigger>
                )}
                {effectiveTabSettings.trophiesEnabled && (
                  <TabsTrigger
                    value="trophies"
                    className="relative overflow-hidden group"
                  >
                    <span className="relative z-10">Trophies</span>
                    <span className="absolute inset-0 bg-primary/10 scale-0 group-hover:scale-100 transition-transform duration-300 rounded-md"></span>
                  </TabsTrigger>
                )}
                {editMode && (
                  <TabsTrigger
                    value="admin"
                    className="relative overflow-hidden group"
                  >
                    <span className="relative z-10">Admin</span>
                    <span className="absolute inset-0 bg-primary/10 scale-0 group-hover:scale-100 transition-transform duration-300 rounded-md"></span>
                  </TabsTrigger>
                )}
              </TabsList>
            )}

            {effectiveTabSettings.tasksEnabled && (
              <TabsContent value="tasks" className="animate-fade-in">
                <TaskList
                  tasks={data.tasks}
                  editMode={editMode}
                  isAnimating={isAnimating}
                  onComplete={handleCompleteTask}
                  onEdit={(task) => handleEditItem(task, "task")}
                  onDelete={(id) => handleDeleteItem(id, "task")}
                  onMoveTask={handleMoveTask}
                  onUpdateStars={handleUpdateStars}
                  onTaskReset={handleResetTask}
                  onAddNew={() => handleAddNew("task")}
                  onResetDaily={handleResetDailyTasks}
                  walletSettings={data.walletSettings}
                />
              </TabsContent>
            )}

            {effectiveTabSettings.rewardsEnabled && (
              <TabsContent value="rewards" className="animate-fade-in">
                <RewardList
                  rewards={data.rewards}
                  availableStars={data.stars}
                  totalStarsEarned={data.totalStarsEarned || 0}
                  editMode={editMode}
                  isAnimating={isAnimating}
                  starHistory={data.starHistory || []}
                  theme={data.theme}
                  onClaim={handleClaimReward}
                  onSpendMoney={handleSpendMoney}
                  onEdit={(reward) => handleEditItem(reward, "reward")}
                  onDelete={(id) => handleDeleteItem(id, "reward")}
                  onMoveUp={(id) => handleMoveReward(id, "up")}
                  onMoveDown={(id) => handleMoveReward(id, "down")}
                  onAddNew={() => handleAddNew("reward")}
                />
              </TabsContent>
            )}

            {data.walletSettings?.enabled &&
              effectiveTabSettings.walletEnabled && (
                <TabsContent value="wallet" className="animate-fade-in">
                  <WalletTab
                    walletSettings={
                      data.walletSettings || {
                        enabled: false,
                        conversionRate: 10,
                      }
                    }
                    walletData={
                      data.walletData || {
                        currentBalance: 0,
                        lifetimeEarned: 0,
                      }
                    }
                    totalStarsEarned={data.totalStarsEarned || 0}
                    currentStars={data.stars}
                    moneyEntries={localMoneyEntries || data.moneyTracking || []}
                    allowAddMoney={effectiveTabSettings.allowAddMoney || false}
                    editMode={editMode}
                    onAddEntry={handleAddMoneyEntry}
                    onUpdateEntry={handleUpdateMoneyEntry}
                    onDeleteEntry={handleDeleteMoneyEntry}
                  />
                </TabsContent>
              )}

            {wishListEnabled && effectiveTabSettings.wishListEnabled && (
              <TabsContent value="wishlist" className="animate-fade-in">
                <WishListTab
                  wishList={wishList}
                  availableStars={data.stars}
                  totalStarsEarned={data.totalStarsEarned || 0}
                  walletData={data.walletData}
                  walletSettings={data.walletSettings}
                  editMode={editMode}
                  onAddItem={addWishItem}
                  onEditItem={updateWishItem}
                  onDeleteItem={deleteWishItem}
                  onMarkAchieved={markWishItemAchieved}
                  onUpdateProgress={updateWishItemProgress}
                />
              </TabsContent>
            )}

            {effectiveTabSettings.trophiesEnabled && (
              <TabsContent value="trophies" className="animate-fade-in">
                <StatsTab
                  data={data}
                  editMode={editMode}
                  onEdit={(achievement) =>
                    handleEditItem(achievement, "achievement")
                  }
                  onDelete={(id) => handleDeleteItem(id, "achievement")}
                  onMoveUp={(id) => handleMoveAchievement(id, "up")}
                  onMoveDown={(id) => handleMoveAchievement(id, "down")}
                  onAddNew={() => handleAddNew("achievement")}
                />
              </TabsContent>
            )}

            {editMode && (
              <TabsContent value="admin" className="animate-fade-in">
                <AdminTab
                  onShowResetDialog={() => setShowResetDialog(true)}
                  onDataSync={handleSyncData}
                  adsEnabled={adsEnabled}
                  onAdsEnabledChange={setAdsEnabled}
                  walletSettings={data.walletSettings}
                  onWalletSettingsChange={handleWalletSettingsChange}
                  bottomNavigation={effectiveBottomNavigation}
                  onBottomNavigationChange={handleBottomNavigationChange}
                  tabSettings={data.tabSettings}
                  onTabSettingsChange={handleTabSettingsChange}
                />
              </TabsContent>
            )}
          </Tabs>

          <StatusBar />
        </div>

        {editMode && (
          <EditModePanel
            onShowResetDialog={() => setShowResetDialog(true)}
            onExitEditMode={() => handleToggleEditMode()}
            bottomNavigation={effectiveBottomNavigation}
          />
        )}

        <BottomAd adsEnabled={adsEnabled} />
      </div>

      <ResetConfirmDialog
        showResetDialog={showResetDialog}
        setShowResetDialog={setShowResetDialog}
        handleResetData={handleResetData}
      />

      <GiantStarAnimation
        isVisible={showGiantStar}
        onAnimationComplete={handleGiantStarAnimationComplete}
      />

      <TrophyNotification
        isVisible={showTrophyNotification}
        trophyTitle={claimedRewardTitle}
        onDismiss={() => setShowTrophyNotification(false)}
      />

      {showPerfectDayCelebration && (
        <PerfectDayCelebration3D
          theme={showPerfectDayCelebration}
          onComplete={() => setShowPerfectDayCelebration(null)}
        />
      )}

      {/* Render bottom navigation when enabled */}
      {effectiveBottomNavigation === true && (
        <BottomTabNavigation
          activeTab={activeTab}
          onTabChange={setActiveTab}
          editMode={editMode}
          walletEnabled={data.walletSettings?.enabled || false}
          tabSettings={effectiveTabSettings}
        />
      )}
    </div>
  );
};

export default StarTracker;
